import { Mail, Phone, MapPin, ShieldCheck } from "lucide-react";

const SITE_URL = "https://hamninstitute.se";

function CvFooter({ accent }) {
  return (
    <div className="mt-6 pt-3 border-t border-neutral-200 text-center">
      <p className="text-[10px] text-neutral-400">
        Detta CV är skapat av{" "}
        <a href={SITE_URL} target="_blank" rel="noopener noreferrer" style={{ color: accent }} className="font-medium hover:underline">
          HAMN CV Studio
        </a>
      </p>
    </div>
  );
}

function Section({ title, children, accent }) {
  return (
    <div className="mb-5">
      <h2 className="font-head text-[13px] font-bold uppercase tracking-wider mb-2" style={{ color: accent }}>{title}</h2>
      <div className="text-[13px] leading-relaxed text-neutral-800">{children}</div>
    </div>
  );
}

const clean = (arr) => (Array.isArray(arr) ? arr.filter((x) => x && String(x).trim()) : []);

export default function CVDocument({ cv, template }) {
  const c = cv.edited_version || cv.ai_version || {};
  const contact = cv.contact || {};
  const t = template || cv.template || "modern";

  const hex = typeof cv.accent_color === "string" && /^#[0-9A-Fa-f]{6}$/.test(cv.accent_color) ? cv.accent_color : null;
  const accent = hex || (t === "klassisk" ? "#1a1a1a" : t === "grafisk" ? "#1E5F8C" : "#2E5C4B");
  const layout = cv.layout === "sidebar" || cv.layout === "standard" ? cv.layout : t === "sidebar" ? "sidebar" : "standard";
  const nameSize = t === "kompakt" ? "text-2xl" : "text-3xl";
  const gap = t === "kompakt" ? "mb-3" : "mb-5";

  const comps = clean(c.core_competencies);
  const exps = (Array.isArray(c.work_experience) ? c.work_experience : []).filter((e) => !e.hidden);
  const edus = Array.isArray(c.education) ? c.education : [];
  const systems = clean(c.systems_tools);
  const certs = clean(c.certifications);
  const langs = clean(c.languages);
  const strengths = clean(c.personal_strengths);
  const verified = clean(c.reference_verified);

  const Header = t === "grafisk" ? (
    <div className={gap}>
      <div className="rounded-xl px-5 py-4 text-white" style={{ backgroundColor: accent }}>
        <h1 className={`font-head ${nameSize} font-bold leading-none`}>{contact.full_name || "Ditt namn"}</h1>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[12px] text-white/85">
          {contact.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{contact.email}</span>}
          {contact.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{contact.phone}</span>}
          {contact.city && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{contact.city}</span>}
        </div>
      </div>
    </div>
  ) : (
    <div className={`${gap} ${t === "modern" ? "border-l-4 pl-4" : ""}`} style={t === "modern" ? { borderColor: accent } : {}}>
      <h1 className={`font-head ${nameSize} font-bold text-neutral-900 leading-none`}>{contact.full_name || "Ditt namn"}</h1>
      {c.professional_profile && t === "klassisk" && <div className="h-[2px] w-16 bg-neutral-900 mt-2" />}
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[12px] text-neutral-600">
        {contact.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{contact.email}</span>}
        {contact.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{contact.phone}</span>}
        {contact.city && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{contact.city}</span>}
      </div>
    </div>
  );

  const ProfileSec = c.professional_profile && (
    <Section title="Professionell profil" accent={accent}><p>{c.professional_profile}</p></Section>
  );
  const CompsSec = comps.length > 0 && (
    <Section title="Kärnkompetenser" accent={accent}>
      <div className="flex flex-wrap gap-x-4 gap-y-1">{comps.map((x, i) => <span key={i}>• {x}</span>)}</div>
    </Section>
  );
  const ExpSec = exps.length > 0 && (
    <Section title="Arbetslivserfarenhet" accent={accent}>
      <div className="space-y-3">
        {exps.map((e, i) => (
          <div key={i}>
            <div className="flex justify-between items-baseline gap-2">
              <span className="font-semibold">{e.role}{e.employer ? `, ${e.employer}` : ""}</span>
              <span className="text-[12px] text-neutral-500 shrink-0">{e.period}</span>
            </div>
            {clean(e.bullets).length > 0 && (
              <ul className="list-disc ml-5 mt-1 space-y-0.5">{clean(e.bullets).map((b, j) => <li key={j}>{b}</li>)}</ul>
            )}
          </div>
        ))}
      </div>
    </Section>
  );
  const EduSec = edus.length > 0 && (
    <Section title="Utbildning" accent={accent}>
      <div className="space-y-2">
        {edus.map((e, i) => (
          <div key={i} className="flex justify-between items-baseline gap-2">
            <span><span className="font-semibold">{e.name}</span>{e.school ? `, ${e.school}` : ""}{e.note ? ` – ${e.note}` : ""}</span>
            <span className="text-[12px] text-neutral-500 shrink-0">{e.period}</span>
          </div>
        ))}
      </div>
    </Section>
  );
  const SystemsSec = systems.length > 0 && <Section title="System och verktyg" accent={accent}>{systems.join(" · ")}</Section>;
  const CertsSec = certs.length > 0 && <Section title="Certifieringar" accent={accent}>{certs.join(" · ")}</Section>;
  const LangsSec = langs.length > 0 && <Section title="Språk" accent={accent}>{langs.join(" · ")}</Section>;
  const StrengthsSec = strengths.length > 0 && <Section title="Personliga styrkor" accent={accent}>{strengths.join(" · ")}</Section>;
  const VerifiedSec = verified.length > 0 && (
    <Section title="Verifierat av referenser" accent={accent}>
      <ul className="space-y-1">
        {verified.map((v, i) => (
          <li key={i} className="flex items-start gap-1.5">
            <ShieldCheck className="h-[13px] w-[13px] shrink-0 mt-[2px]" style={{ color: accent }} />
            <span>{v}</span>
          </li>
        ))}
      </ul>
    </Section>
  );

  if (layout === "sidebar") {
    const SideBlock = ({ title, children }) => (
      <div className="mb-5">
        <h2 className="font-head text-[12px] font-bold uppercase tracking-wider mb-2 text-white/95">{title}</h2>
        <div className="text-[12px] leading-relaxed text-white/85">{children}</div>
      </div>
    );
    return (
      <div className="text-neutral-900 flex" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        <aside className="w-[34%] shrink-0 p-5 text-white" style={{ backgroundColor: accent }}>
          {cv.photo_data && (
            <img src={cv.photo_data} alt="" data-testid="cv-photo-sidebar"
              className="w-24 h-24 rounded-full object-cover mb-3 ring-2 ring-white/40" />
          )}
          <h1 className="font-head text-2xl font-bold leading-tight mb-3">{contact.full_name || "Ditt namn"}</h1>
          <SideBlock title="Kontakt">
            <div className="space-y-1">
              {contact.email && <div className="flex items-center gap-1.5 break-all"><Mail className="h-3 w-3 shrink-0" />{contact.email}</div>}
              {contact.phone && <div className="flex items-center gap-1.5"><Phone className="h-3 w-3 shrink-0" />{contact.phone}</div>}
              {contact.city && <div className="flex items-center gap-1.5"><MapPin className="h-3 w-3 shrink-0" />{contact.city}</div>}
            </div>
          </SideBlock>
          {comps.length > 0 && <SideBlock title="Kärnkompetenser"><ul className="space-y-0.5">{comps.map((x, i) => <li key={i}>• {x}</li>)}</ul></SideBlock>}
          {systems.length > 0 && <SideBlock title="System och verktyg">{systems.join(" · ")}</SideBlock>}
          {langs.length > 0 && <SideBlock title="Språk">{langs.join(" · ")}</SideBlock>}
          {certs.length > 0 && <SideBlock title="Certifieringar">{certs.join(" · ")}</SideBlock>}
          {strengths.length > 0 && <SideBlock title="Personliga styrkor">{strengths.join(" · ")}</SideBlock>}
        </aside>
        <div className="flex-1 p-5">
          {ProfileSec}
          {ExpSec}
          {EduSec}
          {VerifiedSec}
          <CvFooter accent={accent} />
        </div>
      </div>
    );
  }

  return (
    <div className="text-neutral-900" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {Header}
      {ProfileSec}
      {CompsSec}
      {ExpSec}
      {EduSec}
      {SystemsSec}
      {CertsSec}
      {LangsSec}
      {StrengthsSec}
      {VerifiedSec}
      <CvFooter accent={accent} />
    </div>
  );
}
